import { uniqueId } from "lodash";
import React from "react";
import { FieldDefinition, FieldOption } from ".";
import OptionsControl from "./OptionsControl";

export const defaultfieldModel: FieldDefinition = {
  label: "",
  type: "text",
  options: [],
  scope: "conversation",
  persistent: false,
  description: ""
};

interface Props {
  field: FieldDefinition;
  onChange(field: Partial<FieldDefinition>): void;
}

export default function FieldForm({ field, onChange }: Props) {
  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="label" className="block text-sm font-medium mb-1">
          Label
        </label>
        <input
          type="text"
          id="label"
          className="input w-full"
          autoFocus
          placeholder="Enter a field label"
          value={field.label}
          onChange={(e) => onChange({ label: e.currentTarget.value })}
        />
        {field.label && (
          <p className="text-xs text-gray-400 mt-1">
            Variable name:{" "}
            <code className="bg-gray-100 px-1 rounded font-mono">
              {"<<"}
              {field.label.toLowerCase().replace(/\s+/g, "_")}
              {">>"}
            </code>
          </p>
        )}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium mb-1">
          Description
          <span className="text-gray-400 font-normal ml-1">(optional)</span>
        </label>
        <input
          type="text"
          id="description"
          className="input w-full"
          placeholder="Internal description for this field"
          value={field.description ?? ""}
          onChange={(e) => onChange({ description: e.currentTarget.value })}
        />
      </div>

      <div>
        <label htmlFor="type" className="block text-sm font-medium mb-1">
          Type
        </label>
        <select
          id="type"
          className="input w-full"
          value={field.type}
          onChange={(e) =>
            onChange({ type: e.currentTarget.value as FieldDefinition["type"] })
          }
        >
          <option value="text">Textbox</option>
          <option value="checkbox">Checkbox</option>
          <option value="textarea">Textarea</option>
          <option value="select">Select List</option>
        </select>
      </div>

      {field.type === "select" && (
        <div>
          <OptionsControl
            options={field.options}
            onChange={(o: Array<FieldOption>) => onChange({ options: o })}
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-2">Scope</label>
        <div className="space-y-2">
          <ScopeToggle
            checked={field.scope === "contact"}
            label="Contact Attribute"
            helpText="Saved forever with this customer across all conversations. Use for: email, name, preferences, customer tier."
            onChange={() => onChange({ scope: "contact" })}
          />
          <ScopeToggle
            checked={field.scope === "conversation"}
            label="Conversation Attribute"
            helpText="Saved for this conversation, shared across all workflows in this chat. Use for: ticket numbers, issue category, current request."
            onChange={() => onChange({ scope: "conversation" })}
          />
          <ScopeToggle
            checked={field.scope === "workflow"}
            label="Workflow Attribute"
            helpText="Temporary and isolated to this workflow run only. Use for: counters, calculations, integrations."
            onChange={() => onChange({ scope: "workflow" })}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={field.persistent ?? false}
            onChange={(e) => onChange({ persistent: e.currentTarget.checked })}
            id="persistent"
          />
          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cornflower-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cornflower-blue-500"></div>
        </label>
        <div>
          <label
            htmlFor="persistent"
            className="text-sm font-medium cursor-pointer"
          >
            Persistent
          </label>
          <p className="text-xs text-gray-400">
            Value survives workflow completion and carries across language
            switches.
          </p>
        </div>
      </div>
    </div>
  );
}

interface ScopeToggleProps {
  checked: boolean;
  label: string;
  helpText: string;
  onChange(): void;
}

function ScopeToggle({ checked, label, helpText, onChange }: ScopeToggleProps) {
  return (
    <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg border border-gray-200 hover:border-cornflower-blue-300 transition-colors">
      <input
        type="radio"
        name="scope"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 accent-cornflower-blue-500"
      />
      <div>
        <span className="text-sm font-medium">{label}</span>
        <p className="text-xs text-gray-400">{helpText}</p>
      </div>
    </label>
  );
}
